/**
 * List parameter chip (S10) — static or query-list options; single-select
 * commits on click, multi-select commits on Apply (one tile re-run per
 * apply, not per checkbox). "All" is a real selection (PARAM_ALL sentinel):
 * the predicate is ELIDED server-side, documented here as just "All".
 */
import { useEffect, useState } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { BoundParams } from '../../types/dashboardTypes';
import { PARAM_ALL } from '../../services/parameterBinding';
import { ParamChipButton } from './ParamChipButton';
import type { ParamOption } from './useParamOptions';

type Props = {
  id: string;
  label: string;
  options: ParamOption[];
  isLoading?: boolean;
  optionsError?: string;
  multi: boolean;
  includeAll: boolean;
  defaultValue?: string | string[];
  value: BoundParams[string] | undefined;
  onChange: (next: string | string[]) => void;
};

const toArray = (v: Props['value'] | Props['defaultValue']): string[] =>
  v === undefined || (typeof v === 'object' && !Array.isArray(v))
    ? []
    : Array.isArray(v)
      ? v.map(String)
      : [String(v)];

const summarize = (selected: string[], options: ParamOption[]): string => {
  if (selected.includes(PARAM_ALL)) return 'All';
  if (selected.length === 0) return '—';
  const labels = selected.map((v) => options.find((o) => o.value === v)?.label ?? v);
  return labels.length <= 2 ? labels.join(', ') : `${labels[0]} +${labels.length - 1}`;
};

export function ListParamChip({
  id,
  label,
  options,
  isLoading,
  optionsError,
  multi,
  includeAll,
  defaultValue,
  value,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const committed = toArray(value ?? defaultValue);
  const [draft, setDraft] = useState<string[]>(committed);
  useEffect(() => {
    if (open) setDraft(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset draft on open only
  }, [open]);

  const commitSingle = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const toggleDraft = (v: string) => {
    setDraft((prev) => {
      const without = prev.filter((x) => x !== v && x !== PARAM_ALL);
      return prev.includes(v) ? without : [...without, v];
    });
  };

  const allSelected = draft.includes(PARAM_ALL);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ParamChipButton
          icon={<ListFilter className="h-3 w-3" />}
          label={label}
          value={summarize(committed, options)}
          testId={`param-chip-${id}`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5" align="start">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <RandomDotmSpinner size={16} />
          </div>
        ) : optionsError ? (
          <p className="px-2 py-3 text-xs text-muted-foreground" data-testid={`param-options-error-${id}`}>
            {optionsError}
          </p>
        ) : (
          <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
            {includeAll && (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                data-testid={`param-option-all-${id}`}
                onClick={multi ? undefined : () => commitSingle(PARAM_ALL)}
              >
                {multi ? (
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() => setDraft(allSelected ? [] : [PARAM_ALL])}
                  />
                ) : null}
                <span className="font-medium">All</span>
              </label>
            )}
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                data-testid={`param-option-${id}-${option.value}`}
                onClick={multi ? undefined : () => commitSingle(option.value)}
              >
                {multi ? (
                  <Checkbox
                    checked={draft.includes(option.value)}
                    onCheckedChange={() => toggleDraft(option.value)}
                  />
                ) : null}
                <span className={!multi && committed.includes(option.value) ? 'font-medium' : undefined}>
                  {option.label}
                </span>
              </label>
            ))}
            {options.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">No values</p>
            )}
            {multi && (
              <div className="border-t border-border p-1.5">
                <Button
                  size="sm"
                  className="h-6 w-full text-xs"
                  disabled={draft.length === 0}
                  data-testid={`param-apply-${id}`}
                  onClick={() => {
                    onChange(draft);
                    setOpen(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
